import { useCallback, useMemo } from "react";
import { TrashIcon } from "@heroicons/react/outline";

import {
  NormalizedMemberData,
  NormalizedGroupedInvite,
  isWorkspaceInvite,
} from "../../hooks/utils";
import { useOrganizationMembersActions } from "../../hooks/useOrganizationMembersActions";

import { useRemoveMemberFromWorkspace } from "./useRemoveMemberFromWorkspace";

import { Action, DropdownMenuVariant } from "@/components/ui/DropdownMenu";
import { ConfirmationModalVariant } from "@/components/ConfirmationModal";
import { capitalize } from "@/utils/strings";
import { Workspace } from "@/types/workspaces";
import { useConfirm } from "@/context/confirm-context";
import type { OrganizationRole } from "@/types/organization-roles";

interface UseMemberDetailActionsParams {
  member: NormalizedMemberData | null;
  orgMember: NormalizedMemberData | null;
  groupedInvite: NormalizedGroupedInvite | null;
  isOrgOwner: boolean;
  isViewingSelf: boolean;
  organizationId: number;
  organizationRoles: { id: number; name: string }[] | undefined;
  onOpenChange: (open: boolean) => void;
  setSelectedWorkspace: (workspace: Workspace | null) => void;
  setIsManageRolesOpen: (open: boolean) => void;
  onMemberDelete?: () => void;
  onInviteDelete?: () => void;
  onWorkspaceRemoved?: (workspace: Workspace) => void;
  onOrganizationRoleUpdated?: (newRole: OrganizationRole) => void;
}

export const useMemberDetailActions = ({
  member,
  orgMember,
  groupedInvite,
  isOrgOwner,
  isViewingSelf,
  organizationId,
  organizationRoles,
  onOpenChange,
  setSelectedWorkspace,
  setIsManageRolesOpen,
  onMemberDelete,
  onInviteDelete,
  onWorkspaceRemoved,
  onOrganizationRoleUpdated,
}: UseMemberDetailActionsParams) => {
  const { handlers } = useOrganizationMembersActions(
    organizationId,
    onMemberDelete,
    onInviteDelete,
    onOrganizationRoleUpdated,
  );
  const { confirm } = useConfirm();
  const targetUserId = orgMember && "user_id" in orgMember ? orgMember.user.id : 0;
  const { removeMemberFromWorkspace } = useRemoveMemberFromWorkspace({
    targetUserId,
    onSuccess: workspace => {
      onWorkspaceRemoved?.(workspace);
      onMemberDelete?.();
    },
  });

  const handleRemoveFromWorkspace = useCallback(
    async (workspace?: Workspace) => {
      if (!workspace || !orgMember || !("user_id" in orgMember)) {
        return;
      }

      const message = isViewingSelf
        ? "Are you sure you want to leave this workspace?"
        : `Are you sure you want to remove ${orgMember.user.email} from ${workspace.name}?`;

      const isConfirmed = await confirm?.({
        message,
        headerVariant: ConfirmationModalVariant.destructive,
        saveButtonVariant: "destructiveOutline",
        exitButtonVariant: "outline",
      });

      if (isConfirmed) {
        await removeMemberFromWorkspace(workspace);
      }
    },
    [orgMember, isViewingSelf, confirm, removeMemberFromWorkspace],
  );

  const handleManageRoles = useCallback(
    (workspace: Workspace) => {
      setSelectedWorkspace(workspace);
      setIsManageRolesOpen(true);
    },
    [setSelectedWorkspace, setIsManageRolesOpen],
  );

  const handleDeleteWithConfirm = useCallback(async () => {
    if (!member) {
      return;
    }

    let message: string;
    if (isWorkspaceInvite(member)) {
      message =
        member.invites.length > 1
          ? `Are you sure you want to cancel all ${member.invites.length} invites for ${member.invitee_email}?`
          : `Are you sure you want to cancel the invite for ${member.invitee_email}?`;
    } else if (isViewingSelf) {
      message = "Are you sure you want to leave this organization? This action cannot be undone.";
    } else {
      const om = orgMember && "user_id" in orgMember ? orgMember : null;
      message = `Are you sure you want to remove ${om?.user.email} from the organization?`;
    }

    const isConfirmed = await confirm?.({
      message,
      headerVariant: ConfirmationModalVariant.destructive,
      saveButtonVariant: "destructiveOutline",
      exitButtonVariant: "outline",
    });

    if (isConfirmed) {
      handlers.handleDeleteMember(member);
      onOpenChange(false);
    }
  }, [member, orgMember, isViewingSelf, confirm, handlers, onOpenChange]);

  const handleRoleChange = useCallback(
    (roleId: string) => {
      if (!orgMember || !("user_id" in orgMember)) {
        return;
      }
      handlers.handleRoleChange(orgMember, roleId);
    },
    [orgMember, handlers],
  );

  const userOwnerRoles = useMemo(
    () =>
      organizationRoles?.filter(
        (role: { name: string }) =>
          role.name.toLowerCase() === "user" || role.name.toLowerCase() === "owner",
      ) ?? [],
    [organizationRoles],
  );

  const deleteButtonLabel = useMemo(() => {
    if (groupedInvite) {
      return groupedInvite.invites.length > 1 ? "Cancel all invitations" : "Cancel invitation";
    }
    if (isViewingSelf) {
      return "Leave organization";
    }
    return "Remove from organization";
  }, [groupedInvite, isViewingSelf]);

  const userInfoActions: Action<undefined>[] = useMemo(() => {
    const items: Action<undefined>[] = [];

    if (orgMember && "user_id" in orgMember && isOrgOwner && userOwnerRoles.length > 0) {
      userOwnerRoles.forEach((role: { id: number; name: string }) => {
        const isCurrentRole = orgMember.organization_role.id === role.id;
        items.push({
          id: `role-${role.id}`,
          label: `Change to ${capitalize(role.name)}`,
          getDisabled: () => isCurrentRole,
          onClick: () => handleRoleChange(role.id.toString()),
        });
      });
    }

    items.push({
      id: "delete",
      label: deleteButtonLabel,
      icon: TrashIcon,
      variant: DropdownMenuVariant.DESTRUCTIVE,
      getDisabled: () => !isOrgOwner && !isViewingSelf,
      tooltipContent:
        !isOrgOwner && !isViewingSelf ? "Only organization owners can remove members" : undefined,
      onClick: () => handleDeleteWithConfirm(),
    });

    return items;
  }, [
    orgMember,
    isOrgOwner,
    isViewingSelf,
    userOwnerRoles,
    deleteButtonLabel,
    handleRoleChange,
    handleDeleteWithConfirm,
  ]);

  return {
    handlers,
    handleRemoveFromWorkspace,
    handleManageRoles,
    handleDeleteWithConfirm,
    handleRoleChange,
    userOwnerRoles,
    deleteButtonLabel,
    userInfoActions,
  };
};
